// src/pages/GarageRegister.js
import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import { useFormInput } from "@/utils";
import { Loading, Toast, Confirm } from "@/components";
import SearchAddress from "@/components/Modal/Common/SearchAddress";
import { useGetMyRentCorpQuery } from "@/redux/rentCorpApi";
import { usePostCarLocationMutation, usePutCarLocationMutation, useGetCarLocationByIdQuery } from "@/redux/carLocationApi";

export const GarageRegister = () => {
    const { locationId } = useParams();
    const navigate = useNavigate();

    const { formData, handleChange, setFormData } = useFormInput({
        mode: "CREATE", // "CREATE" / "UPDATE"
        toastMsg: "",
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        addressPopupStatus: false,
        id: null,

        // 차고지 등록을 위한 필드
        name: "", // 차고지명
        rentCorpId: 0, // 사업자 ID
        totalArea: null, // 인가면적
        usingArea: null, // 사용면적
        address: "",
        addressDetail: "",
        bCode: "",
        normalCount: null, // 승용 사용 수
        smallCount: null, // 소형 사용 수
        bigCount: null, // 중형 사용 수
        lessor: "", // 임대인
        lessorPhone: "", // 임대인 연락처
    });

    const { mode, toastMsg, confirmStatus, addressPopupStatus, id } = formData;

    // locationId 존재 시, mode = "UPDATE"
    useEffect(() => {
        if (locationId) {
            setFormData((prev) => ({
                ...prev,
                mode: "UPDATE",
                id: locationId,
            }));
        }
    }, [locationId, setFormData]);

    // 차고지 상세 조회: locationId가 있을 때만 호출
    const {
        data: carLocationDetail,
        isLoading: isDetailLoading,
        error: detailError,
    } = useGetCarLocationByIdQuery(locationId, {
        skip: !locationId, // locationId 없으면 skip
    });

    // 상세 조회 응답이 오면 formData에 매핑
    useEffect(() => {
        if (carLocationDetail && carLocationDetail.data) {
            const d = carLocationDetail.data;
            // 서버 응답(예시) => { id, name, rentCorpId, totalArea, usingArea, address, ... }
            setFormData((prev) => ({
                ...prev,
                name: d.name || "",
                rentCorpId: d.rentCorpId || 0,
                totalArea: d.totalArea || null,
                usingArea: d.usingArea || null,
                address: d.address || "",
                addressDetail: d.addressDetail || "",
                bCode: d.bCode || "",
                normalCount: d.normalCount || null,
                smallCount: d.smallCount || null,
                bigCount: d.bigCount || null,
                lessor: d.lessor || "",
                lessorPhone: d.lessorPhone || "",
            }));
        }
    }, [carLocationDetail, setFormData]);

    // 렌트사 조회 (사업자 선택)
    const { data: myRentCorpData } = useGetMyRentCorpQuery();
    const corpOptions =
        myRentCorpData?.data?.map((item) => ({
            value: item.id,
            label: item.corpName,
        })) || [];

    // POST 차고지 등록
    const [postCarLocation, { isLoading: isPostLoading }] = usePostCarLocationMutation();
    const [putCarLocation, { isLoading: isPutLoading }] = usePutCarLocationMutation();

    const handleSaveConfirm = async () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: { confirmPopupStatus: false },
        }));

        try {
            const requestBody = {
                name: formData.name,
                rentCorpId: formData.rentCorpId,
                totalArea: formData.totalArea,
                usingArea: formData.usingArea,
                address: formData.address,
                addressDetail: formData.addressDetail,
                bCode: formData.bCode,
                normalCount: formData.normalCount,
                smallCount: formData.smallCount,
                bigCount: formData.bigCount,
                lessor: formData.lessor,
                lessorPhone: formData.lessorPhone,
            };

            if (mode === "UPDATE") {
                await putCarLocation({
                    carLocationId: formData.id,
                    body: requestBody,
                }).unwrap();
            } else {
                await postCarLocation(requestBody).unwrap();
            }

            if (mode === "CREATE") {
                setFormData((prev) => ({
                    ...prev,
                    isLoading: false,
                    confirmStatus: {
                        confirmPopupStatus: true,
                        confirmTitle: "등록이 완료되었습니다.",
                        confirmDescription: "",
                        confirmButtons: [
                            {
                                label: "확인",
                                className: "btn-primary",
                                onClick: () => navigate("/vehicle/garage"),
                            },
                        ],
                    },
                }));
            } else {
                setFormData((prev) => ({
                    ...prev,
                    toastMsg: "수정이 완료되었습니다.",
                    isLoading: false,
                }));
            }
        } catch (error) {
            console.error(error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "에러 발생",
            }));
        }
    };

    const handleSave = () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: `차고지 정보를 ${mode === "CREATE" ? "등록" : "수정"}하시겠습니까?`,
                confirmDescription: "",
                confirmButtons: [
                    { label: "예", className: "btn-primary", onClick: handleSaveConfirm },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev2) => ({
                                ...prev2,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    const handleDeleteConfirm = async () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: { confirmPopupStatus: false },
            toastMsg: "삭제 기능은 미구현 상태입니다.",
        }));
    };

    const handleDelete = () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: "해당 차고지 정보를 삭제하시겠습니까?",
                confirmDescription: "삭제 후에는 복구할 수 없습니다.",
                confirmButtons: [
                    { label: "예", className: "btn-primary", onClick: handleDeleteConfirm },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev2) => ({
                                ...prev2,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    return (
        <>
            {toastMsg && <Toast visible={true} msg={toastMsg} setFormData={setFormData} />}

            <div className="management-form container">
                <div className="form-container">
                    <div className="form-head">
                        <div className="head-tit">
                            <span className="form_name">{mode === "CREATE" ? "차고지 등록" : "차고지 수정"}</span>
                            <span className="form_desc"></span>
                        </div>
                        <div className="head-options">
                            {mode === "UPDATE" && (
                                <button type="button" className="btn btn-delete" onClick={handleDelete}>
                                    차고지 삭제
                                </button>
                            )}
                            {/* <button type="button" className="btn btn-outline" onClick={handleDelete}>
                                기본값 불러오기
                            </button> */}
                            <button type="button" className="btn btn-save" onClick={handleSave}>
                                {mode === "CREATE" ? "등록" : "수정"}
                            </button>
                        </div>
                    </div>

                    <div className="form-content">
                        <div className="form-wrapper">
                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">차고지 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <GarageInfo
                                            formData={formData}
                                            handleChange={handleChange}
                                            setFormData={setFormData}
                                            corpOptions={corpOptions}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirm 팝업 */}
            {confirmStatus.confirmPopupStatus && (
                <Confirm
                    layer={true}
                    onClose={() =>
                        setFormData((prev) => ({
                            ...prev,
                            confirmStatus: { confirmPopupStatus: false },
                        }))
                    }
                    title={confirmStatus.confirmTitle}
                    description={confirmStatus.confirmDescription}
                    buttons={confirmStatus.confirmButtons}
                />
            )}

            {/* 주소 검색 팝업 */}
            {addressPopupStatus && (
                <SearchAddress
                    modalStatus={addressPopupStatus}
                    setModalStatus={() =>
                        setFormData((prev) => ({
                            ...prev,
                            addressPopupStatus: false,
                        }))
                    }
                    setAddress={(data) =>
                        setFormData((prev) => ({
                            ...prev,
                            address: data.address,
                            bCode: data.bcode,
                        }))
                    }
                />
            )}
        </>
    );
};

// --------------------------------------------------------
// GarageInfo Component
// --------------------------------------------------------
const GarageInfo = ({ formData, handleChange, setFormData, corpOptions }) => {
    const {
        name,
        rentCorpId,
        totalArea,
        usingArea,
        address,
        addressDetail,
        bCode,
        normalCount,
        smallCount,
        bigCount,
        lessor,
        lessorPhone,
    } = formData;

    return (
        <>
            {/* 차고지명 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>차고지명</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="name" placeholder="차고지명" value={name || ""} onChange={handleChange} />
                    </div>
                </div>
            </div>

            {/* 인가 면적 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>인가 면적</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="number" name="totalArea" placeholder="인가 면적" value={totalArea || ""} onChange={handleChange} />
                        <span className="input-esset">㎡</span>
                    </div>
                </div>
            </div>

            {/* 사용 면적 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>사용 면적</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="number" name="usingArea" placeholder="사용 면적" value={usingArea || ""} onChange={handleChange} />
                        <span className="input-esset">㎡</span>
                    </div>
                </div>
            </div>

            {/* 승용 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>승용</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="number" name="normalCount" placeholder="승용" value={normalCount || ""} onChange={handleChange} />
                        <span className="input-esset">대</span>
                    </div>
                </div>
            </div>

            {/* 소형 승합 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>소형 승합</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="number" name="smallCount" placeholder="소형 승합" value={smallCount || ""} onChange={handleChange} />
                        <span className="input-esset">대</span>
                    </div>
                </div>
            </div>

            {/* 중형 승합 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>중형 승합</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="number" name="bigCount" placeholder="중형 승합" value={bigCount || ""} onChange={handleChange} />
                        <span className="input-esset">대</span>
                    </div>
                </div>
            </div>

            {/* 임대인 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>임대인</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="lessor" placeholder="임대인" value={lessor || ""} onChange={handleChange} />
                    </div>
                </div>
            </div>

            {/* 임대인 연락처 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>임대인 연락처</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="lessorPhone"
                            placeholder="임대인 연락처"
                            value={lessorPhone || ""}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            {/* 사업자 선택 (렌트카 업체) */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>사업자 선택</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={corpOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="사업자 선택"
                            value={corpOptions?.find((opt) => opt.value === rentCorpId)}
                            onChange={(option) => setFormData((prev) => ({ ...prev, rentCorpId: option.value }))}
                        />
                    </div>
                </div>
            </div>

            {/* 차고지 주소 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>차고지 주소</span>
                </div>
                <div className="row-content">
                    <div className="form-element" style={{ width: "200px" }}>
                        {/* 우편번호 or 법정동코드 */}
                        <input
                            type="text"
                            name="bCode"
                            placeholder=""
                            value={bCode || ""}
                            onChange={handleChange}
                            readOnly
                            onClick={() =>
                                setFormData((prev) => ({
                                    ...prev,
                                    addressPopupStatus: true,
                                }))
                            }
                        />
                    </div>
                    <div className="form-element" style={{ width: "468px" }}>
                        <input
                            type="text"
                            name="address"
                            placeholder="주소"
                            value={address || ""}
                            onChange={handleChange}
                            readOnly
                            onClick={() =>
                                setFormData((prev) => ({
                                    ...prev,
                                    addressPopupStatus: true,
                                }))
                            }
                        />
                    </div>
                    <div className="form-element">
                        <input
                            type="text"
                            name="addressDetail"
                            placeholder="상세 주소"
                            value={addressDetail || ""}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="form-element_button">
                        <button
                            type="button"
                            className="btn-form_element btn-outline"
                            onClick={() =>
                                setFormData((prev) => ({
                                    ...prev,
                                    addressPopupStatus: true,
                                }))
                            }
                        >
                            주소검색
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};
